import { Alert, Box, Button, Card, Group, Stack, TextInput, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useCreateProduct } from '../api/products';

export function ProductCreatePage() {
  const navigate = useNavigate();
  const create = useCreateProduct();

  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!code.trim() || !displayName.trim()) {
      setError('Code and display name are required.');
      return;
    }

    create.mutate(
      {
        code: code.trim().toLowerCase(),
        display_name: displayName.trim(),
      },
      {
        onSuccess: (product) => {
          notifications.show({ color: 'green', message: 'Product created.' });
          navigate(`/products/${product.id}`);
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            const parsed = (() => {
              try {
                return JSON.parse(err.body) as { message?: string };
              } catch {
                return null;
              }
            })();
            setError(parsed?.message ?? 'Could not create product.');
          } else {
            setError('Could not create product.');
          }
        },
      },
    );
  }

  return (
    <Stack gap="lg">
      <Title order={2}>Create product</Title>
      <Card withBorder padding="lg" maw={680}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack gap="md">
            {error && (
              <Alert color="red" icon={<IconAlertTriangle size={18} />}>
                {error}
              </Alert>
            )}
            <TextInput
              label="Code"
              required
              description="Lowercase alphanumeric with optional dashes. Cannot be changed later. Platform-unique."
              value={code}
              onChange={(e) => setCode(e.currentTarget.value)}
              disabled={create.isPending}
            />
            <TextInput
              label="Display name"
              required
              value={displayName}
              onChange={(e) => setDisplayName(e.currentTarget.value)}
              disabled={create.isPending}
            />
            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" component={Link} to="/products">
                Cancel
              </Button>
              <Button type="submit" loading={create.isPending}>
                Create
              </Button>
            </Group>
          </Stack>
        </Box>
      </Card>
    </Stack>
  );
}
